'use client';

import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { usePwaInstall } from '@/lib/hooks/usePwaInstall';

interface InstallCardProps {
  isDark: boolean;
}

// Business-dashboard-only install entry point (never rendered on public
// customer booking pages). Renders nothing until platform/display-mode is
// known (mounted) and nothing at all on desktop — only a genuine iOS
// device or an Android/Chromium session that has actually fired
// beforeinstallprompt gets a CTA, so this never shows a phone-Home-Screen
// instruction where it wouldn't apply.
export default function InstallCard({ isDark }: InstallCardProps) {
  const { t } = useLanguage();
  const tp = t.pwa;
  const { mounted, isStandalone, isIOS, isAndroid, canInstallAndroid, promptInstall } = usePwaInstall();
  const [showIosSheet, setShowIosSheet] = useState(false);

  if (!mounted) return null;

  const panel = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)';
  const border = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.09)';
  const text = isDark ? '#fff' : '#1a1a2e';
  const textMuted = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)';

  if (isStandalone) {
    // Very subtle installed state — not the full card.
    return (
      <p style={{ color: '#34d399', fontSize: '12.5px', fontWeight: 500, marginBottom: '16px' }}>
        {tp.dashboardCard.installedLabel}
      </p>
    );
  }

  const androidEligible = isAndroid && canInstallAndroid;
  if (!isIOS && !androidEligible) {
    // Desktop, or an Android session where beforeinstallprompt hasn't
    // (yet) fired — no working install action to offer, so show nothing
    // rather than a CTA that would do nothing on click.
    return null;
  }

  return (
    <>
      <div
        style={{
          background: panel,
          border: `1px solid ${border}`,
          borderRadius: '14px',
          padding: '16px 18px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '14px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          <span
            style={{
              fontSize: '20px',
              flexShrink: 0,
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, rgba(124,58,237,0.25), rgba(217,119,6,0.2))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            📲
          </span>
          <div style={{ minWidth: 0 }}>
            <p style={{ color: text, fontSize: '14px', fontWeight: 600 }}>{tp.dashboardCard.title}</p>
            <p style={{ color: textMuted, fontSize: '12.5px', marginTop: '2px' }}>{tp.dashboardCard.body}</p>
          </div>
        </div>
        <button
          onClick={() => (isIOS ? setShowIosSheet(true) : promptInstall())}
          style={{
            background: 'linear-gradient(135deg, #7C3AED, #5D0AF4)',
            border: 'none',
            borderRadius: '9px',
            padding: '9px 18px',
            color: '#fff',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {isIOS ? tp.dashboardCard.iosCta : tp.dashboardCard.androidCta}
        </button>
      </div>

      {showIosSheet && (
        <IosInstallSheet isDark={isDark} onClose={() => setShowIosSheet(false)} />
      )}
    </>
  );
}

export function IosInstallSheet({ isDark, onClose }: { isDark: boolean; onClose: () => void }) {
  const { t } = useLanguage();
  const tp = t.pwa.iosSheet;
  const modalBg = isDark ? '#0F0820' : '#ffffff';
  const text = isDark ? '#fff' : '#1a1a2e';
  const textMuted = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 200, padding: '0' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: modalBg,
          borderRadius: '20px 20px 0 0',
          padding: '26px 22px 30px',
          width: '100%',
          maxWidth: '420px',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.4)',
        }}
      >
        <h3 style={{ color: text, fontSize: '16px', fontWeight: 700, marginBottom: '16px' }}>{tp.title}</h3>
        <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {[tp.step1, tp.step2, tp.step3].map((step, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span
                style={{
                  flexShrink: 0,
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: 'rgba(124,58,237,0.18)',
                  color: '#a78bfa',
                  fontSize: '12px',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {i + 1}
              </span>
              <span style={{ color: text, fontSize: '14px' }}>{step}</span>
            </li>
          ))}
        </ol>
        <p style={{ color: textMuted, fontSize: '12.5px', marginTop: '16px' }}>{tp.footer}</p>
        <button
          onClick={onClose}
          style={{
            marginTop: '18px',
            width: '100%',
            background: 'rgba(124,58,237,0.15)',
            border: '1px solid rgba(124,58,237,0.3)',
            borderRadius: '10px',
            padding: '11px',
            color: '#a78bfa',
            fontSize: '13.5px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {t.common.close}
        </button>
      </div>
    </div>
  );
}
